import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { reportTrackingAtom } from '@/atoms/report-progress.atom';
import { execute } from '@/graphql/execute';
import { useDialog } from '@/providers/DialogProvider';
import { startPdfReport } from '@/queries/report.query';

import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

interface Props {
  projectId: string;
  layerId: string;
  layerName: string;
}

const SUPPORTED_LOCALES = ['en', 'de', 'es', 'fr', 'hu'] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export function GenerateReportDialog({ projectId, layerId, layerName }: Props) {
  const { t, i18n } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const [, setTrackers] = useAtom(reportTrackingAtom);

  const schema = useMemo(
    () =>
      z.object({
        title: z.string().min(1),
        notes: z.string().optional(),
        locale: z.enum(SUPPORTED_LOCALES),
      }),
    [],
  );
  type FormData = z.infer<typeof schema>;

  const currentLocale: SupportedLocale = (SUPPORTED_LOCALES as readonly string[]).includes(i18n.language)
    ? (i18n.language as SupportedLocale)
    : 'en';

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: layerName,
      notes: '',
      locale: currentLocale,
    },
  });

  const mutation = useMutation({
    mutationFn: (vars: FormData) =>
      execute(startPdfReport, {
        input: {
          projectId,
          locale: vars.locale,
          title: vars.title,
          notes: vars.notes || null,
          sections: [{ type: 'DEVIATION', deviation: { layerId } }],
        },
      }),
    onSuccess: (data, vars) => {
      const batchId = data.startPdfReport;
      const toastId = toast.loading(t('report.runningToast', { title: vars.title }), { duration: Infinity });
      setTrackers((prev) => [...prev, { toastId, batchId, projectId, title: vars.title }]);
      dismissActiveDialog();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('report.failedToast'));
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('report.dialogTitle')}</DialogTitle>
        <DialogDescription>{t('report.dialogDescription')}</DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className='flex flex-col gap-3'>
          <FormField
            control={form.control}
            name='title'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('report.titleLabel')}</FormLabel>
                <FormControl>
                  <Input type='text' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='notes'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('report.notesLabel')}</FormLabel>
                <FormControl>
                  <Input type='text' {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='locale'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('report.languageLabel')}</FormLabel>
                <FormControl>
                  <select className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2' {...field}>
                    <option value='en'>English</option>
                    <option value='de'>Deutsch</option>
                    <option value='es'>Español</option>
                    <option value='fr'>Français</option>
                    <option value='hu'>Magyar</option>
                  </select>
                </FormControl>
              </FormItem>
            )}
          />

          <DialogFooter>
            <Button
              variant='outline'
              type='button'
              onClick={() => dismissActiveDialog()}
              disabled={mutation.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type='submit' disabled={mutation.isPending}>
              {mutation.isPending ? t('report.generating') : t('report.generate')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
